import React, { useState } from 'react';
import { ArrowLeft, PieChart as PieIcon, Layers } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';

interface Props {
  onBack: () => void;
}

const COLORS = ['#10B981', '#001F3F', '#2B8CEE', '#87CEEB', '#f59e0b', '#ef4444'];

export const PortfolioAnalyzerScreen: React.FC<Props> = ({ onBack }) => {
  const [allocations, setAllocations] = useState([
    { name: 'US Stocks', value: 50 },
    { name: 'Intl Stocks', value: 20 },
    { name: 'Bonds', value: 20 },
    { name: 'Crypto', value: 5 },
    { name: 'Cash', value: 5 },
  ]);

  const handleSliderChange = (index: number, newValue: number) => {
    const newAllocations = [...allocations];
    newAllocations[index].value = newValue;
    setAllocations(newAllocations);
  };

  const total = allocations.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 animate-fade-in pb-20">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={20} />
        Back to Dashboard
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-emerald/10 rounded-xl text-emerald">
            <PieIcon size={32} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">Asset Mix</h1>
            <p className="text-slate-400 font-inter uppercase text-[10px] font-bold tracking-widest">Portfolio Analyzer</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          {/* Chart */}
          <div className="h-80 relative w-full">
             <ResponsiveContainer width="100%" height="100%">
               <PieChart>
                 <Pie
                   data={allocations}
                   innerRadius={60}
                   outerRadius={100}
                   paddingAngle={5}
                   dataKey="value"
                 >
                   {allocations.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="rgba(0,0,0,0)" />
                   ))}
                 </Pie>
                 <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', color: '#fff' }}/>
                 <Legend verticalAlign="bottom" height={36}/>
               </PieChart>
             </ResponsiveContainer>
             {/* Center Text */}
             <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -mt-4 text-center pointer-events-none">
                <div className="text-[10px] font-inter text-slate-400 font-bold uppercase tracking-widest">TOTAL</div>
                <div className={`text-xl font-bold ${total === 100 ? 'text-emerald' : 'text-red-500'}`}>{total}%</div>
             </div>
          </div>

          {/* Sliders */}
          <div className="space-y-4">
             {allocations.map((asset, index) => (
               <div key={asset.name} className="bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                 <div className="flex justify-between items-center mb-2">
                   <div className="flex items-center gap-2">
                     <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                     <span className="text-sm font-bold text-slate-200">{asset.name}</span>
                   </div>
                   <span className="text-sm font-mono text-slate-400">{asset.value}%</span>
                 </div>
                 <input 
                   type="range" 
                   min="0" max="100" 
                   value={asset.value}
                   onChange={(e) => handleSliderChange(index, Number(e.target.value))}
                   className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                   style={{ accentColor: COLORS[index] }}
                 />
               </div>
             ))}
             
             {total !== 100 && (
               <div className="text-center text-xs text-red-400 font-bold bg-red-900/20 p-2 rounded">
                 Allocations must sum to 100% (Current: {total}%)
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};